import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import convertMassVolume from '../hooks/massVolumeConversion';
import { getIngredientImageUrl } from '../utils/imageUtils';

const MassVolumeConversionPopup = ({ show, onHide, initialUnit, initialQuantity, density, ingredientName, ingredientId }) => {
    const [conversions, setConversions] = useState({});

    // Format number to remove unnecessary decimal places or convert to fractions
    const formatNumber = (num) => {
        if (num === undefined || num === null || isNaN(num)) return '';
        
        const allowedFractions = [
            { fraction: 1/16, label: "1/16", vulgar: "¹⁄₁₆" },
            { fraction: 1/8, label: "1/8", vulgar: "⅛" },
            { fraction: 1/6, label: "1/6", vulgar: "⅙" },
            { fraction: 1/4, label: "1/4", vulgar: "¼" },
            { fraction: 1/3, label: "1/3", vulgar: "⅓" },
            { fraction: 1/2, label: "1/2", vulgar: "½" },
            { fraction: 5/8, label: "5/8", vulgar: "⅝" },
            { fraction: 2/3, label: "2/3", vulgar: "⅔" },
            { fraction: 3/4, label: "3/4", vulgar: "¾" },
            { fraction: 5/6, label: "5/6", vulgar: "⅚" },
        ];

        const parsed = parseFloat(num);
        if (isNaN(parsed)) return '';

        const fractionTolerance = 0.008; // Tolerance to catch common rounding (e.g., 0.33 → 1/3, 0.66 → 2/3)
        const wholeTolerance = 0.02; // Larger tolerance for whole numbers (e.g., 0.99 → 1, 1.98 → 2)
        const whole = Math.floor(parsed);
        const decimal = parsed - whole;

        // Check if it's basically a whole number (including cases like 0.99 or 1.01)
        if (decimal < wholeTolerance || decimal > (1 - wholeTolerance)) {
            return `${decimal > (1 - wholeTolerance) ? whole + 1 : whole}`;
        }

        // Check for fraction match
        const match = allowedFractions.find(f => Math.abs(decimal - f.fraction) < fractionTolerance);

        if (match) {
            const fractionSpan = <span style={{ fontSize: '1.3em' }}>{match.vulgar}</span>;
            if (whole > 0) {
                return <>{whole} {fractionSpan}</>;
            }
            return fractionSpan;
        }

        // Fall back to decimal, cleaned up
        const rounded = parsed.toFixed(3);
        return rounded.replace(/\.?0+$/, '');
    };

    // Calculate conversions when props change
    React.useEffect(() => {
        if (initialUnit && initialQuantity) {
            const results = convertMassVolume(initialUnit, initialQuantity, density);
            setConversions(results);
        }
    }, [initialUnit, initialQuantity, density]);

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton style={{ borderBottom: '2px solid #52cd92' }}>
                <Modal.Title style={{ color: '#333', fontSize: '1.3rem' }}>
                    Unit Conversion
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '1.5rem' }}>
                <div className="mb-4 text-center" style={{ 
                    // backgroundColor: '#f8f9fa', 
                    padding: '1rem', 
                    borderRadius: '8px',
                    // border: '1px solid #e9ecef'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: '0.75rem',
                        marginBottom: '0.25rem'
                    }}>
                        <img
                            src={getIngredientImageUrl(ingredientId)}
                            alt={ingredientName}
                            style={{ 
                                width: '40px', 
                                height: '40px', 
                                objectFit: 'cover', 
                                borderRadius: '4px',
                                background: '#ffffff',
                                // border: '1px solid #dee2e6'
                            }}
                            onError={e => { 
                                e.target.onerror = null; 
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMTIgMTZIMTZWMTlIMjFDMjIuMTA0IDE5IDIyLjg5NiAxOC4yMDggMjIuODk2IDE3VjE1QzIyLjg5NiAxMy43OTIgMjIuMTA0IDEzIDIxIDEzSDE2VjE2SDEyWiIgZmlsbD0iIzkwOTA5MCIvPjwvc3ZnPg=='; 
                            }}
                        />
                        <div style={{ fontSize: '1.1rem', color: '#666' }}>
                            <strong style={{ color: '#52cd92', fontSize: '1.3rem' }}>
                                {initialQuantity} {initialUnit}
                            </strong>
                            {' '}of{' '}
                            <strong style={{ color: '#333' }}>{ingredientName}</strong>
                        </div>
                    </div>
                </div>

                <div className="row g-3">
                    <div className="col-6">
                        <div style={{ 
                            padding: '1rem', 
                            borderRadius: '8px',
                            border: '1px solid #ffe0e0',
                            height: '100%'
                        }}>
                            <div style={{ 
                                fontSize: '0.85rem', 
                                fontWeight: 'bold', 
                                color: '#d63384',
                                marginBottom: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Mass
                            </div>
                            <ul className="list-unstyled" style={{ marginBottom: 0 }}>
                                {[
                                    { value: conversions.g, unit: 'g' },
                                    { value: conversions.kg, unit: 'kg' },
                                    { value: conversions.oz, unit: 'oz' },
                                    { value: conversions.lb, unit: 'lb' }
                                ].map(({ value, unit }) => (
                                    <li key={unit} style={{ 
                                        padding: '0.35rem 0',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'baseline',
                                        borderBottom: '1px solid #ffe0e0'
                                    }}>
                                        <span style={{ 
                                            fontSize: '1rem', 
                                            fontWeight: '600',
                                            color: '#333'
                                        }}>
                                            {formatNumber(value)}
                                        </span>
                                        <span style={{ 
                                            fontSize: '0.85rem', 
                                            color: '#666',
                                            marginLeft: '0.5rem'
                                        }}>
                                            {unit}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="col-6">
                        <div style={{ 
                            padding: '1rem', 
                            borderRadius: '8px',
                            border: '1px solid #cce7ff',
                            height: '100%'
                        }}>
                            <div style={{ 
                                fontSize: '0.85rem', 
                                fontWeight: 'bold', 
                                color: '#0d6efd',
                                marginBottom: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Volume
                            </div>
                            <ul className="list-unstyled" style={{ marginBottom: 0 }}>
                                {[
                                    { value: conversions.cup, unit: 'cups' },
                                    { value: conversions.tbsp, unit: 'tbsp' },
                                    { value: conversions.tsp, unit: 'tsp' },
                                    { value: conversions['fl oz'], unit: 'fl oz' },
                                    { value: conversions.ml, unit: 'ml' },
                                    { value: conversions.l, unit: 'l' }
                                ].map(({ value, unit }) => (
                                    <li key={unit} style={{ 
                                        padding: '0.35rem 0',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'baseline',
                                        borderBottom: '1px solid #cce7ff'
                                    }}>
                                        <span style={{ 
                                            fontSize: '1rem', 
                                            fontWeight: '600',
                                            color: '#333'
                                        }}>
                                            {formatNumber(value)}
                                        </span>
                                        <span style={{ 
                                            fontSize: '0.85rem', 
                                            color: '#666',
                                            marginLeft: '0.5rem'
                                        }}>
                                            {unit}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="text-center mt-3">
                    These conversions are approximate and may vary depending on the ingredient.
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default MassVolumeConversionPopup;
